import { useEffect } from 'react';

import { getAssetPath } from '@/constants/configs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const cv: any;

/**
 * Dynamically injects a <script> tag and resolves when it has loaded.
 * If the script was already injected, resolves immediately.
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

// ── Module-level singleton ──────────────────────────────────
// Shared across all callers so the heavy work only happens once.
let loadPromise: Promise<void> | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let jscanifyInstance: any = null;

/** Returns the preloaded jscanify instance, or `null` if not yet ready. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPreloadedScanner(): any | null {
  return jscanifyInstance;
}

/** Returns the existing load promise, or `null` if preloading hasn't started. */
export function getPreloadPromise(): Promise<void> | null {
  return loadPromise;
}

/**
 * Kick-starts OpenCV + jscanify loading as a fire-and-forget side-effect.
 *
 * Call this hook early (e.g. on the page that *leads to* the scanner) so that
 * by the time the user clicks "Capture with Camera" the WASM runtime and
 * jscanify are already initialised.
 */
export function usePreloadOpenCV(): void {
  useEffect(() => {
    if (loadPromise) return; // already started

    loadPromise = (async () => {
      try {
        await loadScript(getAssetPath('assets/js/opencv.js'));

        // cv.then() resolves when the WASM runtime is ready, or immediately
        // if onRuntimeInitialized has already fired.
        if (typeof cv !== 'undefined' && typeof cv.then === 'function') {
          await new Promise<void>(resolve => {
            cv.then(() => resolve());
          });
        }

        const jscanifyModule = await import('jscanify/client');
        const JscanifyClass = jscanifyModule.default || jscanifyModule;
        jscanifyInstance = new JscanifyClass();
      } catch (e) {
        // Reset so a future call can retry
        loadPromise = null;
        console.error('Failed to preload OpenCV:', e);
      }
    })();
  }, []);
}
